// @ts-nocheck
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react'
import axios from 'axios'
import authService from '../api/api-authorization/AuthorizeService'

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------
const INITIAL_STATE = {
    user: null,
    token: null,
    isAuthenticated: false,
    isInitialized: false,   // true once the first token fetch has settled
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function authReducer(state, action) {
    switch (action.type) {
        case 'INIT_SUCCESS':
            return {
                ...state,
                user: action.user,
                token: action.token,
                isAuthenticated: true,
                isInitialized: true,
            }
        case 'INIT_FAIL':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                isInitialized: true,
            }
        case 'LOGOUT':
            return {
                ...INITIAL_STATE,
                isInitialized: true,
            }
        default:
            return state
    }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext({
    ...INITIAL_STATE,
    signOut: () => {},
})

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
let _axiosInterceptorId = null

export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, INITIAL_STATE)

    // Initialise auth state once on mount.
    useEffect(() => {
        let cancelled = false

        async function init() {
            const token = await authService.getAccessToken()
            if (cancelled) return

            if (!token) {
                dispatch({ type: 'INIT_FAIL' })
                return
            }

            const user = await authService.getUser()
            if (cancelled) return

            // Install axios Bearer interceptor (idempotent — remove old one first).
            if (_axiosInterceptorId !== null) {
                axios.interceptors.request.eject(_axiosInterceptorId)
            }
            _axiosInterceptorId = axios.interceptors.request.use(config => {
                const current = authService._token
                if (current) {
                    config.headers = config.headers ?? {}
                    config.headers.Authorization = `Bearer ${current}`
                }
                return config
            })

            dispatch({ type: 'INIT_SUCCESS', user, token })
        }

        init()
        return () => { cancelled = true }
    }, [])

    const signOut = useCallback(async () => {
        dispatch({ type: 'LOGOUT' })
        localStorage.clear()
        // Clear session: navigate to server logout endpoint which signs out the cookie.
        window.location.href = '/Identity/Account/Logout'
    }, [])

    return (
        <AuthContext.Provider value={{ ...state, signOut }}>
            {children}
        </AuthContext.Provider>
    )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useAuth() {
    return useContext(AuthContext)
}

export default AuthContext
